import { FC, useEffect, useMemo, useRef, useState } from "react";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import {
  NO_STATUS_FILTER,
  StatusCodeFilter,
  statusCodeIsPinned,
  statusFilterKey,
} from "../statusCodeFilter";
import { eventIconUrl } from "./eventIcons";

type LatLng = { lat: number; lng: number };

export type MiniMapRoute = {
  path: LatLng[];
  color: string;
  points: VehicleMapDataPoint[];
};

type Props = {
  // usually one, but a card comparing a whole asset holds that asset's
  // whole day - each route coloured separately so they stay distinguishable
  routes: MiniMapRoute[];
  showEvents: boolean;
  // which event types get a pin; null means pin everything and an empty
  // array means pin nothing, the same convention the full maps use
  filteredStatusCodes?: StatusCodeFilter;
};

// a small, non-interactive, self-fitting map - a genuine google.maps.Map
// instance (same technique as the overlap lens), not a static image, so it
// stays crisp and matches the real route.
export const TripMiniMap: FC<Props> = ({
  routes,
  showEvents,
  filteredStatusCodes = NO_STATUS_FILTER,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const polylinesRef = useRef<any[]>([]);
  const markersRef = useRef<any[]>([]);
  const [isMapReady, setIsMapReady] = useState(false);

  // created once and reused. the routes array is rebuilt on every parent
  // render, so keying the effect on it rebuilt the whole map each time.
  useEffect(() => {
    const google = (window as any).google;
    if (!google || !containerRef.current || mapRef.current) return;

    const map = new google.maps.Map(containerRef.current, {
      disableDefaultUI: true,
      draggable: false,
      scrollwheel: false,
      keyboardShortcuts: false,
      clickableIcons: false,
      gestureHandling: "none",
      zoomControl: false,
    });
    mapRef.current = map;
    // wait for tiles, or the route draws over a blank grey square
    google.maps.event.addListenerOnce(map, "idle", () => setIsMapReady(true));
  }, []);

  // identity-independent key, so redraws follow the routes actually changing
  const routesKey = useMemo(
    () =>
      routes
        .map((route) => {
          if (route.path.length === 0) return `${route.color}:empty`;
          const first = route.path[0];
          const last = route.path[route.path.length - 1];
          return `${route.color}:${route.path.length}:${first.lat},${first.lng}:${last.lat},${last.lng}`;
        })
        .join("|"),
    [routes]
  );

  useEffect(() => {
    const google = (window as any).google;
    const map = mapRef.current;
    if (!google || !map || !isMapReady) return;

    polylinesRef.current.forEach((polyline) => polyline.setMap(null));
    polylinesRef.current = [];

    const bounds = new google.maps.LatLngBounds();
    let hasAny = false;

    routes.forEach((route) => {
      if (route.path.length === 0) return;
      polylinesRef.current.push(
        new google.maps.Polyline({
          path: route.path,
          map,
          strokeColor: route.color,
          strokeOpacity: 0.9,
          strokeWeight: 3,
        })
      );
      route.path.forEach((point) => {
        bounds.extend(point);
        hasAny = true;
      });
    });

    if (hasAny) map.fitBounds(bounds, 28);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady, routesKey]);

  useEffect(() => {
    const google = (window as any).google;
    const map = mapRef.current;
    if (!google || !map || !isMapReady) return;

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
    if (!showEvents) return;

    routes.forEach((route) => {
      route.points.forEach((point) => {
        if (!statusCodeIsPinned(filteredStatusCodes, point.statuscode)) return;
        markersRef.current.push(
          new google.maps.Marker({
            position: { lat: point.latitude, lng: point.longitude },
            map,
            icon: {
              url: eventIconUrl(point, 18),
              scaledSize: new google.maps.Size(18, 18),
              anchor: new google.maps.Point(9, 9),
            },
            zIndex: 20,
          })
        );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // joined rather than the array itself: the caller rebuilds it on every
    // render, and identity alone would redraw every pin each time
  }, [isMapReady, showEvents, routesKey, statusFilterKey(filteredStatusCodes)]);

  useEffect(
    () => () => {
      polylinesRef.current.forEach((polyline) => polyline.setMap(null));
      markersRef.current.forEach((marker) => marker.setMap(null));
    },
    []
  );

  return <div ref={containerRef} className="w-full h-full" />;
};
